import { useState } from 'react'
import { useAuthActions } from '@convex-dev/auth/react'
import { Loader2, ArrowLeft } from 'lucide-react'
import { isElectron } from '@/lib/electron'
import { track } from '@/lib/track'
import { cn } from '@/lib/utils'

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
)

export type AuthMode = 'login' | 'signup'

interface AuthFormProps {
  mode: AuthMode
  /** Where Google OAuth returns to. Defaults to /login or /signup based on mode. */
  redirectTo?: string
  /** Tracking location for analytics. */
  trackLocation?: 'modal' | 'signin_page' | 'signup_page'
}

const pillButton =
  'inline-flex items-center justify-center gap-2 w-full h-12 rounded-full text-[15px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
const pillButtonOutline =
  `${pillButton} border border-border bg-background text-foreground hover:bg-muted/60`
const pillButtonSolid =
  `${pillButton} bg-foreground text-background hover:bg-foreground/90`
const pillInput =
  'w-full h-12 rounded-full border border-border bg-background px-5 text-[15px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-foreground/10 focus:border-foreground/30 disabled:opacity-50'

export function AuthForm({ mode, redirectTo, trackLocation }: AuthFormProps) {
  const { signIn } = useAuthActions()
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const defaultLocation = mode === 'signup' ? 'signup_page' : 'signin_page'
  const location = trackLocation ?? defaultLocation
  const defaultRedirect = `${window.location.origin}/auth`

  const trackClicked = () => {
    if (mode === 'signup') {
      track('signup.clicked', { location: location as 'modal' | 'signup_page' })
    } else {
      track('signin.clicked', { location: location as 'modal' | 'signin_page' })
    }
  }

  const handleGoogle = () => {
    trackClicked()
    setIsGoogleLoading(true)
    signIn('google', { redirectTo: redirectTo ?? defaultRedirect })
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    trackClicked()
    setIsLoading(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    try {
      await signIn('resend-otp', { email: email.trim().toLowerCase() })
      setStep('code')
    } catch {
      setErrorMessage('Failed to send code. Please check your email and try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!code.trim()) return
    setIsLoading(true)
    setErrorMessage(null)
    try {
      const result = await signIn('resend-otp', { email: email.trim().toLowerCase(), code: code.trim() })
      if (result?.signingIn) {
        if (mode === 'login') track('signin.completed', { method: 'email' })
        // Hard reload of the current path (sans hash): convex-auth's setToken
        // doesn't trigger the ConvexProviderWithAuth onChange callback when
        // swapping one authenticated token for another (anon → real), so
        // subscribed queries keep their stale identity and the AuthModal
        // never closes. Booting fresh picks up the new JWT cleanly. We strip
        // any `#signin` hash so the modal doesn't immediately re-pop.
        window.location.assign(window.location.pathname + window.location.search)
        return
      }
      setErrorMessage('The code is incorrect or has expired. Please try again.')
    } catch {
      setErrorMessage('The code is incorrect or has expired. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    try {
      await signIn('resend-otp', { email: email.trim().toLowerCase() })
      setCode('')
      setSuccessMessage('New code sent.')
    } catch {
      setErrorMessage('Failed to resend code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const normalizedEmail = email.trim().toLowerCase()

  return (
    <div className="w-full">
      <div className="text-center space-y-3 pt-2 px-2">
        <h2 className="text-[28px] font-semibold leading-tight tracking-tight text-foreground">
          {step === 'code' ? 'Check your email' : 'Sign in'}
        </h2>
        <p className="text-[15px] text-muted-foreground leading-snug max-w-[18rem] mx-auto">
          {step === 'code' ? (
            <>
              Enter the 6-digit code sent to
              <br />
              <span className="font-medium text-foreground break-all">{normalizedEmail}</span>
            </>
          ) : (
            'Create a free account to get started.'
          )}
        </p>
      </div>

      <div className="space-y-3 pt-6">
        {errorMessage && (
          <p className="text-sm text-destructive text-center">{errorMessage}</p>
        )}

        {step === 'email' ? (
          <>
            {!isElectron() && (
              <>
                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={isGoogleLoading || isLoading}
                  className={pillButtonOutline}
                >
                  {isGoogleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
                  Continue with Google
                </button>

                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-card px-3 text-[12px] tracking-wider text-muted-foreground uppercase">
                      or
                    </span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSendCode} className="space-y-4">
              <input
                type="email"
                placeholder="Email address"
                className={pillInput}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
              <button
                type="submit"
                className={cn(pillButtonSolid, 'mt-2')}
                disabled={isLoading || !email.trim()}
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue
              </button>
            </form>
          </>
        ) : (
          <div className="space-y-4">
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                disabled={isLoading}
                autoFocus
                aria-label="Verification code"
                className={cn(
                  pillInput,
                  'text-center font-mono text-[22px] tracking-[0.5em] pl-9 placeholder:font-sans placeholder:tracking-[0.5em]',
                )}
              />
              <button
                type="submit"
                className={pillButtonSolid}
                disabled={isLoading || code.length !== 6}
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify
              </button>
            </form>

            <div className="flex items-center justify-center gap-2 text-[13px] text-muted-foreground">
              <button
                type="button"
                onClick={() => {
                  setStep('email')
                  setCode('')
                  setErrorMessage(null)
                  setSuccessMessage(null)
                }}
                disabled={isLoading}
                className="inline-flex items-center hover:text-foreground transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                Use a different email
              </button>
              <span aria-hidden className="text-muted-foreground/40">·</span>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading}
                className="hover:text-foreground transition-colors disabled:opacity-50"
              >
                Resend code
              </button>
            </div>

            {successMessage && (
              <p className="text-xs text-emerald-600 text-center">{successMessage}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
